/* eslint-disable max-lines-per-function */
import { createStyles, Grow, makeStyles } from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import React from 'react';
import { TransitionGroup } from 'react-transition-group';
import styled from 'styled-components';
import { DSClickAwayListener } from '../../..';
import { DSChip } from '../../../mui/Chip/DSChip';
import { combineClasses } from '../../../utils';
import { OutlinedTextFieldProps, OutlinedTextField } from '../OutlinedTextField';
import { useRemoveIndexFromArray } from './useRemoveIndexFromArray';

const StyledChip = styled(DSChip)<{ $error: boolean; $selected: boolean }>`
    margin-top: 5px;
    margin-bottom: 5px;
    border-radius: 8px;

    ${props =>
        props.$selected &&
        `
        background-color: ${props.theme.palette.primary.main};
        color: #ffffff;
        svg {
            color: #ffffff;
        }
    `}

    &:focus {
        background-color: ${props => props.theme.palette.primary.main};
        color: #ffffff;
        svg {
            color: #ffffff;
        }
    }

    ${props =>
        props.$error &&
        `
        background-color: ${props.theme.palette.error.main};
        color: #ffffff;
        svg {
            color: #ffffff;
        }
        &:focus {
            background-color: ${props.theme.palette.error.dark};
        }
        ${props.$selected && `background-color: ${props.theme.palette.error.dark};`}
    `}
`;

const StyledSpan = styled.span`
    margin-left: 2px;
`;

const useStyles = makeStyles(
    createStyles({
        input: {
            flexGrow: 1,
            marginLeft: 5,
            paddingBottom: 8,
            paddingTop: 8,
            width: 'auto',
        },
        root: {
            flexWrap: 'wrap',
            maxWidth: '100%',
            padding: 5,
        },
    }),
);

export type ChipFieldValueObject<V> = { label: string; key: number | string; value: V; error?: boolean };
export type ChipFieldValueObjectMinimal<V> = Omit<ChipFieldValueObject<V>, 'value'> &
    Partial<Pick<ChipFieldValueObject<V>, 'value'>>;
export type ChipFieldValueString<V> = V extends string ? V : never;

export type ChipFieldValueArray<V = any> =
    | Array<ChipFieldValueObject<V>>
    | (V extends string ? Array<ChipFieldValueString<V>> : never)
    | (V extends undefined ? Array<ChipFieldValueObjectMinimal<V>> : never);

export type ChipFieldValue<T extends ChipFieldValueArray> = T;

export type ChipFieldAddHandler = (inputValue: string) => void;
export type ChipFieldRemoveHandler<V> = (item: V, index: number, wasBackspace: boolean) => void;

function isChipFieldStringItems(items: any): items is string[] {
    return items.length > 0 && typeof items[0] === 'string';
}

type ChipFieldRenderObject<V> = {
    error?: boolean;
    key: number | string;
    label: string;
    value?: V;
};

function valueToRenderable<T extends ChipFieldValueArray>(value: T): Array<ChipFieldRenderObject<T[number]>> {
    if (isChipFieldStringItems(value)) {
        return value.map((val, index) => ({ key: index, label: val, value: val }));
    } else {
        return value;
    }
}

export type ChipFieldProps<V = any, T extends ChipFieldValueArray<V> = ChipFieldValueArray<V>> = {
    InputProps?: OutlinedTextFieldProps['InputProps'];
    clearOnAdd?: boolean;
    disableBlur?: boolean;
    disableEnter?: boolean;
    error?: OutlinedTextFieldProps['error'];
    fullWidth?: OutlinedTextFieldProps['fullWidth'];
    helperText?: OutlinedTextFieldProps['helperText'];
    innerRef?: Exclude<OutlinedTextFieldProps['InputProps'], undefined>['innerRef'];
    inputRef?: Exclude<OutlinedTextFieldProps['InputProps'], undefined>['inputRef'];
    inputValue?: string;
    label?: OutlinedTextFieldProps['label'];
    margin?: OutlinedTextFieldProps['margin'];
    maxChips?: number;
    onAdd?: ChipFieldAddHandler;
    onClick?: () => void;
    onFocus?: () => void;
    onInputValueChange?: (newValue: string) => void;
    onKeyDown?: React.KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>;
    onRemove?: ChipFieldRemoveHandler<V>;
    placeholder?: OutlinedTextFieldProps['placeholder'];
    value?: T;
};

export function ChipField<V, T extends ChipFieldValueArray<V> = ChipFieldValueArray<V>>({
    clearOnAdd = true,
    disableBlur = true,
    disableEnter = true,
    error,
    fullWidth,
    helperText,
    innerRef,
    InputProps,
    inputRef,
    inputValue,
    label,
    margin,
    maxChips,
    onAdd,
    onClick,
    onFocus,
    onInputValueChange,
    onKeyDown,
    onRemove,
    placeholder,
    value = [] as unknown as T,
}: ChipFieldProps<V, T>): JSX.Element {
    const [componentInputValue, setComponentInputValue] = React.useState('');

    const [selectedIndex, setSelectedIndex] = React.useState<number | null>(null);
    const classes = useStyles();
    const [focused, setFocused] = React.useState(false);

    const controlledInputValue = inputValue ?? componentInputValue;
    const setControlledInputValue = React.useCallback(
        (newValue: string) => {
            onInputValueChange?.(newValue);
            setComponentInputValue(newValue);
        },
        [onInputValueChange],
    );

    const selectedLimited = React.useMemo<T>(
        () => (maxChips && !focused ? value.slice(0, maxChips) : value) as unknown as T,
        [focused, maxChips, value],
    );
    const hiddenCount = React.useMemo(
        () => (maxChips && !focused ? value.length - maxChips : 0),
        [focused, maxChips, value.length],
    );
    const valueWithLimited = React.useMemo<T>(() => selectedLimited, [selectedLimited]);

    const tryAdd = React.useCallback(() => {
        const trimmedInputValue = controlledInputValue.trim();
        if (trimmedInputValue.length > 0) {
            onAdd?.(trimmedInputValue);
            if (clearOnAdd) {
                setControlledInputValue('');
            }
        }
    }, [clearOnAdd, controlledInputValue, onAdd, setControlledInputValue]);

    const handleBlur = React.useCallback(() => {
        if (!disableBlur) {
            return;
        }
        setFocused(false);
        setSelectedIndex(null);
        tryAdd();
    }, [disableBlur, tryAdd]);

    const handleFocus = React.useCallback(() => {
        setFocused(true);
        onFocus?.();
    }, [onFocus]);

    const removeItem = React.useCallback<ChipFieldRemoveHandler<V>>(
        (item, index, wasBackspace = false) => {
            onRemove?.(item, index, wasBackspace);
            setSelectedIndex(null);
        },
        [onRemove],
    );

    const handleEnter = React.useCallback(() => {
        if (!disableEnter) {
            return;
        }

        tryAdd();
        setSelectedIndex(null);
    }, [disableEnter, tryAdd]);

    const handleBackspace = React.useCallback(() => {
        if (selectedIndex === null) {
            if (value.length > 0) {
                setSelectedIndex(value.length - 1);
            }
            return;
        }
        const found = value[selectedIndex];
        removeItem(typeof found === 'string' ? (found as V) : (found.value as V), selectedIndex, true);
        setSelectedIndex(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIndex, removeItem, value, value.length]);

    const handleInputChange = React.useCallback<React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>>(
        event => {
            setControlledInputValue(event.target.value);
            setSelectedIndex(null);
        },
        [setControlledInputValue],
    );

    const handleKeyDown = React.useCallback<React.KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>>(
        event => {
            if (event.key === 'Enter') {
                handleEnter();
                event.preventDefault();
            } else if (event.key === 'Backspace') {
                handleBackspace();
            } else if (event.key === 'Escape') {
                handleBlur();
            }
            onKeyDown?.(event);
        },
        [handleBackspace, handleBlur, handleEnter, onKeyDown],
    );

    const renderedItems = React.useMemo(() => {
        const mappedForRender = valueToRenderable<T>(valueWithLimited);
        return (
            <TransitionGroup timeout={100}>
                {mappedForRender.map((option, index) => (
                    <Grow key={option.key}>
                        <StyledChip
                            $error={!!option.error}
                            $selected={selectedIndex === index}
                            color="default"
                            deleteIcon={<ClearIcon />}
                            label={option.label}
                            size="small"
                            title={option.label}
                            onClick={() => {
                                handleFocus();
                            }}
                            onDelete={() => {
                                removeItem(option.value as unknown as V, index, false);
                            }}
                        />
                    </Grow>
                ))}
            </TransitionGroup>
        );
    }, [handleFocus, removeItem, selectedIndex, valueWithLimited]);

    return (
        <DSClickAwayListener onClickAway={handleBlur}>
            <span>
                <OutlinedTextField
                    InputProps={{
                        ...InputProps,
                        classes: combineClasses(classes, InputProps?.classes),
                        innerRef,
                        inputRef,
                        onFocus: handleFocus,
                        onKeyDown: handleKeyDown,
                        startAdornment: (
                            <>
                                {renderedItems}
                                {hiddenCount > 0 && <StyledSpan onClick={handleFocus}>{`+ ${hiddenCount}`}</StyledSpan>}
                            </>
                        ),
                    }}
                    error={error}
                    fullWidth={fullWidth}
                    helperText={helperText}
                    label={label}
                    margin={margin}
                    placeholder={placeholder}
                    value={controlledInputValue}
                    onChange={handleInputChange}
                    onClick={onClick}
                />
            </span>
        </DSClickAwayListener>
    );
}
ChipField.displayName = 'ChipField';
ChipField.useRemoveIndexFromArray = useRemoveIndexFromArray;
